import { Injectable } from "@angular/core";
import { ModalDismissReasons, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { from, mergeMap , tap } from "rxjs";
import { NotificationEvent, SaleRequestNotificationDto, StudySubmittedNotificationDto } from "../models/common/notification-event.model";
import { SaleHorse } from "../models/sales/sale-horse.model";
import { SaleMemberStatus } from "../models/sales/sales.model";
import { ManifestViewerComponent } from "../popups/manifest-viewer/manifest-viewer.component";
import { ReviewVetProfilePopupComponent } from "../profile-page/review-vet-profile-popup/review-vet-profile-popup.component";
import { SaleStudyFilesPopupComponent } from "../sales/sale-study-files-popup/sale-study-files-popup.component";
import { ToastService } from "../shared-new/services/toast.service";
import { StudiesFacade } from "../shared-new/store/studies/studies.facade";
import { EventsEmitter } from "../shared/event-emitter/EventEmitter";
import { SalesService } from "./sales.service";

@Injectable({
  providedIn: 'root',
})

export class PopupService {

  public closeResult = '';

  public constructor(private readonly modalService: NgbModal,
    private studyFacade: StudiesFacade,
    private eventEmitter: EventsEmitter,
    private saleService: SalesService,
    private toastService: ToastService) { }

  public openManifestViewerPopup(data: any): void {
    const modalRef = this.modalService.open(ManifestViewerComponent, {
      ariaLabelledBy: 'Manifest-Viewer-Popup-Id',
      centered: false,
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.result.then(
      (reason: any) => {
       
      }
    );
  }

  reviewVetInfo(request: NotificationEvent<SaleRequestNotificationDto>): void {

    const modalRef = this.modalService.open(ReviewVetProfilePopupComponent, {
      centered: true,
      size: '50-width',
      keyboard: false,
      backdrop: 'static'
    });

    modalRef.componentInstance.data = {
      memberId: request.data.saleMemberRequest.memberId,
      userId: request.data.saleMemberRequest.userId,
      saleId: request.data.saleId,
      // The decision must name the real request; without it the popup is read-only.
      saleMemberRequestId: request.data.saleMemberRequest.saleMemberRequestId
    };

    from(modalRef.result).pipe(
      mergeMap(result => {
        const data = JSON.parse(result);

        if (!('isApproved' in data)) {
          return "";
        }

        return data.isApproved
          ? this.approve(request, data)
          : this.deny(request, data);
      })
    ).subscribe();
  }

  private approve(
    request: NotificationEvent<SaleRequestNotificationDto>,
    data: any
  ) {
    return this.saleService.acceptSaleMemberRequestAccess({
      saleMemberRequestId: request.data.saleMemberRequest.saleMemberRequestId,
      saleMemberStatusId: SaleMemberStatus.ApprovedId,
      comments: data.comments,
      isSubmitAccess: data.isSubmitAccess,
      isViewAccess: data.isViewAccess,
      isAllSaleAccess: data.isAllSaleAccess,
      notification: data.notificationData
    }).pipe(
      tap(() => {
        this.toastService.success('Member request has been approved');
        this.eventEmitter.emitOnSaleRequestApproveEvent(request.data.saleId);
      })
    );
  }

  private deny(
    request: NotificationEvent<SaleRequestNotificationDto>,
    data: any
  ) {
    return this.saleService.acceptSaleMemberRequestAccess({
      saleMemberRequestId: request.data.saleMemberRequest.saleMemberRequestId,
      saleMemberStatusId: SaleMemberStatus.DeniedId,
      comments: data.comments,
      notification: data.notificationData
    }).pipe(
      tap(() => {
        this.toastService.success('Member request has been denied');
        this.eventEmitter.emitOnSaleRequestDeniedEvent(request.data.saleId);
      })
    );
  }

  
  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  public openStudyPopup(notification: NotificationEvent<StudySubmittedNotificationDto>) {
    this.studyFacade.resetData();
    const saleHorse = {
      horseId: notification.scope.horseId,
      saleId: notification.scope.saleId,
    } as SaleHorse;
    this.studyFacade.loadStudyFilesBySaleHorse(saleHorse);

    this.studyFacade.loadStatuses();
    const modalRef = this.modalService.open(SaleStudyFilesPopupComponent, {
      ariaLabelledBy: saleHorse.horseId + 'Id',
      centered: true,
      // A fixed 1040px measure — see .modal-study-files in styles.scss. It was
      // full-width + fullscreen, which is what left the file lists spread across
      // the whole monitor.
      size: 'study-files',
      keyboard: false,
      backdrop: 'static',
    });

    modalRef.componentInstance.data = {
      saleId: saleHorse.saleId,
      horseId: saleHorse.horseId,
      statusId: notification.data.status,
    };
    modalRef.result.then(
      (result) => { },
      (reason) => {
        if (reason) {
          this.eventEmitter.emitOnStudyPopupCloseEvent(saleHorse.saleId);
        }
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }

}
